import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { getAdminCases, type AdminCaseRow } from "@/shared/db";
import { Select } from "@/shared/ui";

export const AdminCasesPage: React.FC = () => {
  const { t } = useTranslation();
  const [cases, setCases] = useState<AdminCaseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const fetchCases = async () => {
    setLoading(true);
    try {
      const data = await getAdminCases({
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 100,
      });
      setCases(data);
    } catch (err) {
      console.error("Error fetching cases:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [statusFilter]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold text-juridico">
          {t("admin.cases.title")}
        </h1>
        <p className="text-juridico/60 mt-1">{t("admin.cases.subtitle")}</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "all", label: t("common.allStatuses") },
              { value: "active", label: t("admin.cases.status.active") },
              { value: "pending", label: t("admin.cases.status.pending") },
              { value: "closed", label: t("admin.cases.status.closed") },
            ]}
          />
          <button
            onClick={fetchCases}
            className="text-sm text-tiber-500 hover:text-tiber-500/80 font-medium"
          >
            {t("common.refresh")}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-romance-200">
              <thead className="bg-romance-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.cases.table.caseNumber")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.cases.table.title")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.cases.table.lawyer")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.cases.table.client")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.cases.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.cases.table.created")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-romance-200">
                {cases.map((c) => (
                  <tr key={c.id || ""} className="hover:bg-romance-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-juridico">{c.case_number || "—"}</td>
                    <td className="px-6 py-4 text-sm font-medium text-juridico">{c.title || "—"}</td>
                    <td className="px-6 py-4 text-sm text-juridico">
                      {c.lawyer_name || c.lawyer_email || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-juridico">
                      {c.client_name || c.client_email || "—"}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-romance-100 text-juridico">
                        {c.status || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-juridico/60">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && cases.length === 0 && (
          <div className="text-center py-12 text-juridico/60">
            {t("admin.cases.noResults")}
          </div>
        )}
      </motion.div>
    </div>
  );
};
